import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BackdropCircular } from 'components';
import {
  Button,
  Divider,
  Grid,
  Typography,
  TextField,
  Icon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Grow,
  makeStyles,
} from '@material-ui/core';
import MUIDataTable from 'mui-datatables';
import {
  getUsersAPI,
  addUserStoreAPI,
  deleteUserStoreAPI,
} from 'api';
import EditUser from './EditUser';
import CustomUsersAutocomplete from './CustomUsersAutocomplete';
import CustomStoresAutocomplete from './CustomStoresAutocomplete';


const useStyles = makeStyles(() => ({
  root: {
    marginTop: '20px',
    '& .MuiTableCell-head, & .MuiTableCell-head': {
      color: '#6a6a6a',
      fontSize: '14px',
      fontWeight: 600
    },
  }
}));

const UserStores = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [userEmail, setUserEmail] = useState();
  const [userStoreUrl, setUsersStoreUrl] = useState();
  const [editedUser, setEditedUser] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [email, setEmail] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const { users, usersTotalCount, stores, updateUserProfile } = useSelector((state) => state.admin);
  const {profile} = useSelector((state) => state.user);
  const permissions = profile?.config?.permissions || {};

  useEffect(() => {
    const params = new URLSearchParams({
      email,
      page: String(page),
      limit: String(limit),
    }).toString();

    getUsersAPI({dispatch, params});
  }, [
    email,
    page,
    limit
  ]);

  const onSearch = (searchValue) => {
    setEmail(searchValue);
    setPage(1);
    setLimit(20);
  };

  const handleUserStoreAdd = () => {
    const store = stores.find((store) => store.apiUrl === userStoreUrl);
    addUserStoreAPI({dispatch, userEmail, store});
  };

  const handleUserStoreRemove = (apiUrl, email) => {
    deleteUserStoreAPI({dispatch, apiUrl, email});
  };

  const usersColumns = [
    {
      name: '_id',
      label: 'User ID',
      options: {}
    },
    {
      name: 'user',
      label: 'User Name',
      options: {
        filter: true,
        setCellHeaderProps: () => ({ style: { width: '150px' } }),
        customBodyRenderLite: (dataIndex) => {
          const user = users[dataIndex];

          return (
            <div className="flex items-center">
              {user.firstName} {user.lastName}
            </div>
          );
        },
      }
    },
    {
      name: 'email',
      label: 'User Email',
      options: {
        setCellHeaderProps: () => ({ style: { width: '250px' } }),
      }
    },
    {
      name: 'storesId',
      label: 'User Store URL',
      options: {
        setCellHeaderProps: () => ({ style: { width: '400px' } }),
        customBodyRenderLite: (dataIndex) => {
          const {stores, email} = users[dataIndex];

          return (
            <div className="items-center">
              {stores && stores.map((store, ind) =>
                (
                  <div key={ind} className="flex justify-between mb-1">
                    <div className="py-1">{store.storeUrl}</div>
                    {permissions?.adminUserStoresRemove &&
                    <Button variant="outlined" color="primary" size="small" onClick={() => handleUserStoreRemove(store.apiUrl, email)}>
                      Remove store
                    </Button>}
                  </div>
                ))}
            </div>
          );
        },
      }
    },
    {
      name: 'edit',
      label: 'Actions',
      options: {
        customBodyRenderLite: (dataIndex) => {
          return (
            <Grid>
              {permissions?.adminUserStoresEdit &&
              <IconButton
                title="Edit user name"
                onClick={() => {
                  setEditedUser(users[dataIndex]);
                }}
              >
                <Icon>edit</Icon>
              </IconButton>}
            </Grid>
          );
        }
      }
    }
  ];

  const options = {
    filterType: 'textField',
    selectableRows: 'none',
    rowsPerPageOptions: [10, 20, 50, 100],
    serverSide: true,
    search: true,
    customSearchRender: (
      searchText,
      handleSearch,
      hideSearch
    ) => {
      return (
        <Grow appear in timeout={300}>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            value={searchValue}
            onChange={({ target: { value } }) => {
              setSearchValue(value);
            }}
            onKeyPress={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                onSearch(searchValue);
              }
            }}
            placeholder="Enter user email"
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => {
                    setSearchValue('');
                    onSearch('');
                    hideSearch();
                  }}
                >
                  <Icon fontSize="small">
                    clear
                  </Icon>
                </IconButton>
              ),
            }}
          />
        </Grow>
      );
    },
    filter: false,
    sort: false,
    download: true,
    print: true,
    viewColumns: true,
    rowsPerPage: limit,
    page: page - 1,
    count: usersTotalCount,
    onChangePage: (page) => setPage(page + 1),
    onChangeRowsPerPage: (newPageSize) => {
      setLimit(newPageSize);
      setPage(1);
    },
    setTableProps: () => ({
      headRow: {
        fontWeight: 'bold',
      },
    }),
  };

  return (
    <div className="flex-grow h-full min-h-full-screen scroll-y pb-25 my-10 ml-10 pr-10">
      {permissions?.adminUserStoresAssign &&
      <div className="mb-10" >
        <Typography variant="h3" className="mb-8">Add Store to User</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={5}>
            <CustomUsersAutocomplete setUserEmail={setUserEmail} />
          </Grid>
          <Grid item xs={12} sm={5}>
            <CustomStoresAutocomplete setUsersStoreUrl={setUsersStoreUrl} />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button variant="contained" color="primary" size="large" className="w-full h-full text-18 uppercase" onClick={handleUserStoreAdd}>Add</Button>
          </Grid>
        </Grid>
      </div>}

      <Divider />
      {permissions?.adminUserStoresCreate &&
        <Button
          variant="contained"
          color="primary"
          size="large"
          className="w-full h-full text-18 uppercase"
          onClick={() => setEditedUser({})}
        >
          Create New User
        </Button>}
      <MUIDataTable
        className={classes.root}
        title={'Users'}
        data={users}
        columns={usersColumns}
        options={options}
      />
      <Divider />

      <Dialog
        open={editedUser}
        onClose={() => setEditedUser(false)}
        aria-label="edit-banner-slide"
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{editedUser.email ? `Edit User ${editedUser.firstName} ${editedUser.lastName}` : 'Create New User'}</DialogTitle>
        <DialogContent>
          <EditUser userData={editedUser} openDialog={setEditedUser} />
        </DialogContent>
      </Dialog>
      <BackdropCircular loading={updateUserProfile} />
    </div>
  );
};

export default UserStores;
